import { useEffect, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import { debounce } from 'debounce';

import Message from 'types/Message';
import SetState from 'types/SetState';
import { appContainerId } from 'consts/app';
import map from 'utils/map';
import useRefBoolean from 'hooks/useRefBoolean';

export type MenuItem<SectionId> = { id: SectionId; label: Message };

type Props<SectionId extends string> = {
  sectionsVisibility?: Partial<Record<SectionId, boolean>>;
  sections: readonly SectionId[];
  labels?: Record<SectionId, Message>;
  offset?: number;
};

type HookResponse<SectionId extends string> = {
  menuItems: MenuItem<SectionId>[];
  activeItemId: SectionId | undefined;
  setActiveItemId: SetState<SectionId | undefined>;
};

const useAnchorState = <SectionId extends string>({
  sectionsVisibility,
  sections,
  labels,
  offset = 0,
}: Props<SectionId>): HookResponse<SectionId> => {
  const router = useRouter();
  const menuItems: MenuItem<SectionId>[] = sections.map((sectionId) => ({
    id: sectionId,
    label: labels?.[sectionId] || sectionId,
  }));
  const filteredMenuItems = menuItems.filter((item) => (sectionsVisibility ? sectionsVisibility?.[item.id] : true));
  const [activeItemId, setActiveItemId] = useState<SectionId | undefined>();
  const [isAfterFirstScroll, { on: startScrolling }] = useRefBoolean();
  const [isNavigating, { on: startNavigating, off: endNavigating }] = useRefBoolean();
  const visibleItems = useMemo(
    () => map<MenuItem<SectionId>, SectionId>(filteredMenuItems, 'id').reverse(),
    [filteredMenuItems]
  );

  useEffect(() => {
    const app = document?.getElementById(appContainerId);
    const onScrollHandler = debounce(() => {
      if (visibleItems.length > 1 && !isNavigating.current) {
        const scrollPosition = app?.scrollTop || 0;
        const activeItem = visibleItems.find((key) => {
          const offsetTop = document.querySelector<HTMLElement>(`[anchor-id="${key}"]`)?.offsetTop || 0;
          return scrollPosition > offsetTop - offset;
        });
        startScrolling();
        setActiveItemId(activeItem);
      }
    }, 100);

    setTimeout(() => {
      app?.addEventListener('scroll', onScrollHandler);
    }, 2000); // Time after initial scroll

    return () => {
      app?.removeEventListener('scroll', onScrollHandler);
    };
  }, [visibleItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handlerOn = startNavigating;
    const handlerOff = endNavigating;

    router.events.on('routeChangeStart', handlerOn);
    router.events.on('routeChangeComplete', handlerOff);
    router.events.on('hashChangeStart', handlerOn);
    router.events.on('hashChangeComplete', handlerOff);

    return () => {
      router.events.off('routeChangeStart', handlerOn);
      router.events.off('routeChangeComplete', handlerOff);
      router.events.off('hashChangeStart', handlerOn);
      router.events.off('hashChangeComplete', handlerOff);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAfterFirstScroll) {
      const [baseUrl] = router.asPath.split('#');
      router.replace(`${baseUrl}${activeItemId ? `#${activeItemId}` : ''}`, undefined, { shallow: true });
    }
  }, [activeItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    menuItems: filteredMenuItems,
    activeItemId,
    setActiveItemId,
  };
};

export default useAnchorState;
